import { useId } from 'react'

import './atoms.css'

/**
 * 🔴 이 프로젝트의 핵심 규약 — 비활성에는 반드시 사유가 붙는다.
 *
 * 권한 없는 버튼을 숨기지 않는다. 비활성 + 사유를 보여준다(README §5-6).
 * 사유 없는 비활성이 애초에 만들어지지 않게 **타입으로 막는다** —
 * `disabled: true` 면 `disabledReason` 이 없으면 컴파일이 실패한다.
 *
 * 사유 문구는 지어내지 말고 `@ctcells/i18n` 의 `reason.*` 키를 쓴다(§5-8).
 */
export type DisabledState =
  | { disabled?: false | undefined; disabledReason?: undefined }
  | { disabled: true; disabledReason: string }

/**
 * 비활성 표현 방식 — `disabled` 속성을 쓰지 않고 `aria-disabled` 로 간다.
 *
 * 왜: `disabled` 속성이 붙은 버튼은 포커스를 받지 못한다. 그러면 키보드·스크린리더
 * 사용자가 **왜 못 누르는지 알 방법이 없다.** 와이어프레임의 접근성 문제 그대로다(§11-4-2·9).
 * 그래서 포커스는 살리고, 활성화만 막고, 사유를 두 경로로 내보낸다.
 *
 *   - `title`           → 마우스 툴팁 (화면 본문에 설명 문장을 쓰지 않는다 · §1-1)
 *   - `aria-describedby` → 시각적으로 숨긴 텍스트. 스크린리더가 읽는다
 */
export function useDisabled(disabled: boolean | undefined, reason: string | undefined) {
  const reasonId = useId()
  const isDisabled = disabled === true
  const hasReason = isDisabled && reason !== undefined && reason !== ''

  return {
    isDisabled,
    /** 상호작용 요소에 그대로 펼친다 */
    ariaProps: {
      'aria-disabled': isDisabled ? true : undefined,
      'aria-describedby': hasReason ? reasonId : undefined,
      title: hasReason ? reason : undefined,
    },
    /** 요소 안(또는 바로 옆)에 렌더한다. 화면에는 안 보이고 스크린리더만 읽는다 */
    reasonNode: hasReason ? (
      <span className="a-sr" id={reasonId}>
        {reason}
      </span>
    ) : null,
  }
}
